import { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { removeMask } from "../../Helpers/Masc";

// Função auxiliar para obter valor aninhado usando notação de ponto
const getNestedValue = (obj, path) => {
    if (!path || !obj) return undefined;

    // Se não contém ponto, é uma propriedade simples
    if (!path.includes(".")) {
        return obj[path];
    }

    // Dividir o caminho e navegar pelo objeto
    const keys = path.split(".");
    let current = obj;

    for (const key of keys) {
        if (current === null || current === undefined) {
            return undefined;
        }
        current = current[key];
    }

    return current;
};

// Função auxiliar para definir valor aninhado usando notação de ponto
const setNestedValue = (obj, path, value) => {
    if (!path) return obj;

    // Se não contém ponto, é uma propriedade simples
    if (!path.includes(".")) {
        return { ...obj, [path]: value };
    }

    // Dividir o caminho
    const keys = path.split(".");
    const result = { ...obj };

    // Remover propriedade plana se existir (evitar duplicatas como address.zip_code)
    if (result[path]) {
        delete result[path];
    }

    let current = result;

    // Navegar até o penúltimo nível
    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];

        // Se a propriedade não existe ou não é um objeto, criar um novo objeto
        if (!current[key] || typeof current[key] !== "object") {
            current[key] = {};
        } else {
            // Fazer uma cópia para manter a imutabilidade
            current[key] = { ...current[key] };
        }

        current = current[key];
    }

    // Definir o valor na última chave
    current[keys[keys.length - 1]] = value;

    return result;
};

export function useForm(formConfig, options = {}) {
    const { initialData = {}, validationRules = {}, onDataChange } = options;
    const { t } = useTranslation();

    // Estado do formulário
    const [formData, setData] = useState(initialData);
    const [errors, setErrors] = useState({});
    const [isLoading] = useState(false); // Por enquanto, apenas retorna false
    const isInitialized = useRef(false);
    const initialDataRef = useRef(initialData);
    // When we programmatically update formData due to initialData sync,
    // we want to avoid notifying the parent via onDataChange to prevent
    // an update loop between parent state and internal form state.
    const skipNotifyRef = useRef(false);

    // Inicializar dados do formulário com valores padrão
    useEffect(() => {
        if (isInitialized.current) return;

        // Começar com os dados iniciais
        let finalData = { ...initialData };

        formConfig.forEach((field) => {
            // Só criar valores padrão para campos que não existem nos initialData
            const currentValue = getNestedValue(initialData, field.name);

            if (currentValue === undefined) {
                switch (field.type) {
                    case "checkbox":
                        finalData = setNestedValue(
                            finalData,
                            field.name,
                            false
                        );
                        break;
                    case "number":
                        finalData = setNestedValue(
                            finalData,
                            field.name,
                            field.min ?? 0
                        );
                        break;
                    case "select":
                        finalData = setNestedValue(
                            finalData,
                            field.name,
                            field.options?.[0]?.value ?? ""
                        );
                        break;
                    default:
                        finalData = setNestedValue(finalData, field.name, "");
                }
            }
        });

        setData(finalData);
        initialDataRef.current = { ...finalData };
        isInitialized.current = true;
        // After initializing from initialData, skip notifying parent once
        skipNotifyRef.current = true;
    }, [formConfig, initialData]);

    // Sincronizar formData quando initialData mudar (para dados carregados assincronamente)
    useEffect(() => {
        if (!isInitialized.current) return;

        // Verificar se há dados significativos no initialData
        const hasSignificantData = Object.values(initialData).some((value) => {
            return value !== "" && value !== null && value !== undefined;
        });

        // Verificar se há mudanças no initialData
        const previousInitialData = initialDataRef.current;
        const hasChanges =
            JSON.stringify(initialData) !== JSON.stringify(previousInitialData);

        // Sincronizar apenas se há dados significativos E mudanças no initialData
        if (hasSignificantData && hasChanges) {
            let mergedData = { ...formData };

            // Para cada campo na configuração, usar setNestedValue para garantir estrutura correta
            formConfig.forEach((field) => {
                const newValue = getNestedValue(initialData, field.name);
                if (newValue !== undefined) {
                    mergedData = setNestedValue(
                        mergedData,
                        field.name,
                        newValue
                    );
                }
            });

            // Só atualiza se o resultado do merge for diferente do estado atual
            if (JSON.stringify(mergedData) !== JSON.stringify(formData)) {
                // When merging in new initialData, skip notifying parent
                skipNotifyRef.current = true;
                setData(mergedData);
            }

            initialDataRef.current = { ...initialData };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData]);

    // Atualizar um campo específico
    const updateField = useCallback(
        (fieldName, value) => {
            // Para campos de radio, converter string para booleano se necessário
            const fieldConfig = formConfig.find(
                (field) => field.name === fieldName
            );
            let processedValue = value;

            if (fieldConfig && fieldConfig.type === "radio") {
                // Se o campo radio tem opções com valores "true"/"false", converter para booleano
                const hasBooleansOptions = fieldConfig.options?.some(
                    (option) =>
                        option.value === "true" || option.value === "false"
                );

                if (hasBooleansOptions) {
                    processedValue = value === "true";
                }
            }

            // Para campos com máscara, remover a máscara antes de salvar
            if (fieldConfig && fieldConfig.mask && typeof value === "string") {
                processedValue = removeMask(value);
            }

            setData((prev) => setNestedValue(prev, fieldName, processedValue));

            // Limpar erro do campo quando o usuário começar a digitar
            setErrors((prev) => {
                if (prev[fieldName]) {
                    return { ...prev, [fieldName]: "" };
                }
                return prev;
            });
        },
        [formConfig]
    );

    // Notificar mudanças via callback
    useEffect(() => {
        // If the change was triggered by initialData sync, skip notifying once
        if (skipNotifyRef.current) {
            skipNotifyRef.current = false;
            return;
        }

        onDataChange?.(formData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData]);

    // Validar um campo específico
    const validateField = useCallback(
        (fieldName) => {
            const fieldConfig = formConfig.find(
                (field) => field.name === fieldName
            );
            if (!fieldConfig) return null;

            const value = getNestedValue(formData, fieldName);
            const errorMessages = fieldConfig.errorMessages || {};

            // Verificar se o campo é obrigatório
            if (fieldConfig.required && (!value || value === "")) {
                return (
                    errorMessages.required ||
                    t("validation.required", {
                        field: fieldConfig.label,
                    }) ||
                    `${fieldConfig.label} é obrigatório`
                );
            }

            // Validação por tipo de campo
            if (value && value !== "") {
                switch (fieldConfig.type) {
                    case "email": {
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (
                            typeof value === "string" &&
                            !emailRegex.test(value)
                        ) {
                            return (
                                errorMessages.email ||
                                t("validation.email") ||
                                "Email inválido"
                            );
                        }
                        break;
                    }

                    case "number": {
                        const numValue = Number(value);
                        if (isNaN(numValue)) {
                            return (
                                errorMessages.number ||
                                t("validation.number") ||
                                "Deve ser um número válido"
                            );
                        }
                        if (
                            fieldConfig.min !== undefined &&
                            numValue < fieldConfig.min
                        ) {
                            return (
                                errorMessages.min ||
                                t("validation.min", {
                                    min: fieldConfig.min,
                                }) ||
                                `Valor mínimo é ${fieldConfig.min}`
                            );
                        }
                        if (
                            fieldConfig.max !== undefined &&
                            numValue > fieldConfig.max
                        ) {
                            return (
                                errorMessages.max ||
                                t("validation.max", {
                                    max: fieldConfig.max,
                                }) ||
                                `Valor máximo é ${fieldConfig.max}`
                            );
                        }
                        break;
                    }

                    case "text":
                    case "textarea":
                        if (typeof value === "string") {
                            if (
                                fieldConfig.minLength &&
                                value.length < fieldConfig.minLength
                            ) {
                                return (
                                    errorMessages.minLength ||
                                    t("validation.minLength", {
                                        minLength: fieldConfig.minLength,
                                    }) ||
                                    `Mínimo de ${fieldConfig.minLength} caracteres`
                                );
                            }
                            if (
                                fieldConfig.maxLength &&
                                value.length > fieldConfig.maxLength
                            ) {
                                return (
                                    errorMessages.maxLength ||
                                    t("validation.maxLength", {
                                        maxLength: fieldConfig.maxLength,
                                    }) ||
                                    `Máximo de ${fieldConfig.maxLength} caracteres`
                                );
                            }
                            if (fieldConfig.pattern) {
                                const regex = new RegExp(fieldConfig.pattern);
                                if (!regex.test(value)) {
                                    return (
                                        errorMessages.pattern ||
                                        t("validation.pattern") ||
                                        "Formato inválido"
                                    );
                                }
                            }
                        }
                        break;
                    default:
                        break;
                }
            }

            // Validação customizada
            if (fieldConfig.validate) {
                const customError = fieldConfig.validate(value);
                if (customError) return customError;
            }

            // Validação via regras externas
            if (validationRules[fieldName]) {
                const ruleError = validationRules[fieldName](value, formData);
                if (ruleError) return ruleError;
            }

            return null;
        },
        [formConfig, validationRules, formData, t]
    );

    // Validar todo o formulário
    const validateForm = useCallback(() => {
        const newErrors = {};
        let hasErrors = false;

        formConfig.forEach((field) => {
            // Verificar se o campo deve ser exibido
            if (field.condition && !field.condition(formData)) {
                return; // Pular validação se o campo não deve ser exibido
            }

            const error = validateField(field.name);
            if (error) {
                newErrors[field.name] = error;
                hasErrors = true;
            }
        });

        setErrors(newErrors);
        return !hasErrors;
    }, [formConfig, formData, validateField]);

    // Resetar formulário
    const reset = useCallback(() => {
        const defaultData = {};

        formConfig.forEach((field) => {
            switch (field.type) {
                case "checkbox":
                    defaultData[field.name] = false;
                    break;
                case "number":
                    defaultData[field.name] = field.min ?? 0;
                    break;
                case "select":
                    defaultData[field.name] = field.options?.[0]?.value ?? "";
                    break;
                default:
                    defaultData[field.name] = "";
            }
        });

        setData({ ...defaultData, ...initialData });
        setErrors({});
    }, [formConfig, initialData]);

    // Versões estáveis dos setters
    const stableSetData = useCallback((data) => {
        setData(data);
    }, []);

    const stableSetErrors = useCallback((errors) => {
        setErrors(errors);
    }, []);

    // Função para obter valor de campo aninhado
    const getFieldValue = useCallback(
        (fieldName) => {
            const value = getNestedValue(formData, fieldName);

            // Para campos de radio, converter booleano para string
            const fieldConfig = formConfig.find(
                (field) => field.name === fieldName
            );
            if (fieldConfig && fieldConfig.type === "radio") {
                return String(value);
            }

            return value;
        },
        [formData, formConfig]
    );

    return {
        formData,
        errors,
        isLoading,
        updateField,
        validateField,
        validateForm,
        reset,
        getFieldValue,
        setData: stableSetData,
        setErrors: stableSetErrors,
    };
}
